import { useDashboard } from '@/hooks/useDashboard'
import { useCurrentOrg, useCurrentOrgId } from '@/hooks/useOrganisations'
import { KpiCard } from '@/components/KpiCard'
import { euro, nlGeheel, nlGetal } from '@/lib/format'

export function DashboardPage() {
  const orgId = useCurrentOrgId()
  const org = useCurrentOrg()
  const { data, isLoading, isError, error } = useDashboard(orgId)

  if (!orgId) {
    return (
      <PageHeader title="Dashboard & Rapportage">
        <Card>Selecteer eerst een klant in de zijbalk.</Card>
      </PageHeader>
    )
  }

  if (isLoading) {
    return (
      <PageHeader title="Dashboard & Rapportage" subtitle={org?.naam}>
        <Card>Cijfers worden geladen…</Card>
      </PageHeader>
    )
  }

  if (isError || !data) {
    return (
      <PageHeader title="Dashboard & Rapportage" subtitle={org?.naam}>
        <Card>
          <p className="text-red-600">
            Kan dashboard niet laden: {(error as Error)?.message ?? 'onbekende fout'}
          </p>
        </Card>
      </PageHeader>
    )
  }

  const { kpi, banksheet, voorraadsamenstelling, tht_buckets, open_facturen, bederfelijke_waar_actief } = data

  return (
    <div>
      <PageHeader
        title="Dashboard & Rapportage"
        subtitle={`${data.organisation_naam} — kerncijfers voor de financier`}
      />

      {/* Aansluiting Financierbaar */}
      <Section title="Aansluiting Financierbaar">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <KpiCard label="Waarderingsom" value={euro(kpi.waarderingsom)} />
          <KpiCard
            label="Deductiesom"
            value={euro(kpi.deductiesom)}
            warning={kpi.deductiesom > 0}
            accent="red"
          />
          <KpiCard label="Waarderingsom financierbaar" value={euro(kpi.waarderingsom_financierbaar)} />
        </div>
        {open_facturen.length > 0 && (
          <details className="mt-3 card p-4">
            <summary className="text-sm font-medium text-kh-gray-700 cursor-pointer">
              Deductiesom — opbouw ({open_facturen.length} open {open_facturen.length === 1 ? 'factuur' : 'facturen'})
            </summary>
            <ul className="mt-2 space-y-1 text-sm">
              {open_facturen.map((f) => (
                <li key={f.factuurnr} className="flex justify-between text-kh-gray-600">
                  <span><strong>{f.factuurnr}</strong></span>
                  <span>{euro(f.bedrag)}</span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </Section>

      {/* Banksheet */}
      <Section title="Financieringsopstelling (banksheet)">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Totale voorraadwaarde" value={euro(banksheet.totaal)} />
          <KpiCard label="Vrije voorraad" value={euro(banksheet.vrije_voorraad)} />
          {bederfelijke_waar_actief && (
            <KpiCard
              label={`Incourant (THT < ${banksheet.min_houdbaarheidsdagen}d)`}
              value={euro(banksheet.incourant)}
              warning={banksheet.incourant > 0}
              accent="red"
            />
          )}
          <KpiCard label="Financierbaar (met %)" value={euro(banksheet.financierbaar)} />
        </div>
      </Section>

      {/* Voorraadsamenstelling */}
      <Section title="Voorraadsamenstelling">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard label="Voorraad op locatie" value={euro(banksheet.liggend)} />
          <KpiCard label="Voorraad onderweg" value={euro(banksheet.zeilend)} />
          <KpiCard label="Aantal partijen" value={nlGeheel(voorraadsamenstelling.aantal_partijen)} accent="blue" />
          <KpiCard label="Totaal eenheden" value={nlGeheel(voorraadsamenstelling.totaal_eenheden)} accent="blue" />
        </div>
      </Section>

      {/* Overige activa */}
      {(banksheet.emballage > 0 || banksheet.verpakking > 0 || banksheet.overig > 0) && (
        <Section title="Overige financierbare activa">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <KpiCard
              label={`Emballage (${banksheet.fin_pct_emballage}%)`}
              value={euro(banksheet.emballage)}
            />
            <KpiCard
              label={`Verpakkingen (${banksheet.fin_pct_verpakking}%)`}
              value={euro(banksheet.verpakking)}
            />
            <KpiCard
              label={`Overig (${banksheet.fin_pct_overig}%)`}
              value={euro(banksheet.overig)}
            />
          </div>
        </Section>
      )}

      {/* Aftrekposten */}
      <Section title="Aftrekposten">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <KpiCard
            label="Openstaande facturen"
            value={euro(banksheet.crediteuren_open)}
            warning={banksheet.crediteuren_open > 0}
            accent="red"
          />
          <KpiCard label="Onderweg zonder factuur" value={euro(banksheet.norf)} />
          {bederfelijke_waar_actief && (
            <KpiCard
              label="Incourant (THT)"
              value={euro(banksheet.incourant)}
              warning={banksheet.incourant > 0}
              accent="red"
            />
          )}
        </div>
      </Section>

      {/* THT verdeling */}
      {bederfelijke_waar_actief && voorraadsamenstelling.aantal_partijen > 0 && (
        <Section title="Houdbaarheidsverdeling">
          <Card noPadding>
            <table className="w-full text-sm">
              <thead className="bg-kh-gray-50 text-kh-gray-600 uppercase text-xs tracking-wider">
                <tr>
                  <th className="text-left  px-4 py-3">Klasse</th>
                  <th className="text-right px-4 py-3">Partijen</th>
                  <th className="text-right px-4 py-3">Waarde</th>
                  <th className="text-right px-4 py-3">% van totaal</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-kh-gray-100">
                {tht_buckets
                  .filter((b) => b.partijen > 0)
                  .map((b) => (
                    <tr key={b.klasse}>
                      <td className="px-4 py-2.5">{b.klasse}</td>
                      <td className="px-4 py-2.5 text-right">{nlGeheel(b.partijen)}</td>
                      <td className="px-4 py-2.5 text-right">{euro(b.waarde)}</td>
                      <td className="px-4 py-2.5 text-right">{nlGetal(b.pct, 1)}%</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </Card>
        </Section>
      )}
    </div>
  )
}

// ─── Kleine helpers ───────────────────────────────────────────────────────

function PageHeader({
  title, subtitle, children,
}: { title: string; subtitle?: string; children?: React.ReactNode }) {
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-kh-blue">{title}</h1>
        {subtitle && <p className="text-sm text-kh-gray-500 mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-6 first:mt-0">
      <h2 className="text-sm font-semibold text-kh-gray-700 mb-3 uppercase tracking-wider">
        {title}
      </h2>
      {children}
    </section>
  )
}

function Card({
  children, noPadding,
}: { children: React.ReactNode; noPadding?: boolean }) {
  return (
    <div className={`card ${noPadding ? '' : 'p-5'}`}>
      {children}
    </div>
  )
}
